import moment from "moment";

/**
 * Shape of `dynamicConfig.requiredDocuments` (served from the mongo config, so
 * the list can change without a deployment):
 *
 * {
 *   "creditReportKey": "creditReport",        // optional, default shown
 *   "creditReportMaxAgeDays": 90,             // optional, default shown
 *   "common": [{ "key": "einLetter", "label": "EIN Letter" }],
 *   "rules": [
 *     {
 *       "when": { "loanPurpose": ["Purchase"], "cashOut": false },
 *       "documents": [{ "key": "liquidity", "label": "Liquidity" }]
 *     }
 *   ]
 * }
 *
 * `common` applies to every loan; each rule whose `when` matches adds its
 * documents. `when.loanPurpose` / `when.cashOut` are both optional (omitted =
 * matches anything). With no config, nothing is required.
 */
export interface RequiredDocument {
  key: string;
  label: string;
}

export interface RequiredDocumentsConfig {
  creditReportKey?: string;
  creditReportMaxAgeDays?: number;
  common?: RequiredDocument[];
  rules?: {
    when?: { loanPurpose?: string[]; cashOut?: boolean };
    documents: RequiredDocument[];
  }[];
}

export const DEFAULT_CREDIT_REPORT_KEY = "creditReport";
export const DEFAULT_CREDIT_REPORT_MAX_AGE_DAYS = 90;

// A Rate/Term Refinance is never treated as cash out, whatever the cash out
// flags say - it always needs the Liquidity document.
export const isCashOutLoan = (loanInformation: any): boolean =>
  loanInformation?.loanPurpose !== "Rate/Term Refinance" &&
  (loanInformation?.loanPurpose === "Cash Out Refinance" ||
    loanInformation?.borrowerProceeds === "Y" ||
    loanInformation?.cashOutFlag === "Cash Out");

export function getRequiredDocuments(
  config: RequiredDocumentsConfig | undefined,
  loanInformation: any,
): RequiredDocument[] {
  if (!config) return [];
  const purpose = loanInformation?.loanPurpose;
  const cashOut = isCashOutLoan(loanInformation);
  const matched = (config.rules ?? []).filter(({ when }) => {
    if (when?.loanPurpose && !when.loanPurpose.includes(purpose)) return false;
    if (when?.cashOut !== undefined && when.cashOut !== cashOut) return false;
    return true;
  });
  const all = [
    ...(config.common ?? []),
    ...matched.flatMap((rule) => rule.documents ?? []),
  ];
  // de-duplicate by key in case rules overlap
  return [...new Map(all.map((d) => [d.key, d])).values()];
}

export const getUploadedRequiredDocument = (
  documents: any[] | undefined,
  key: string,
) => documents?.find((d) => !d?.isDeleted && d?.requiredDocumentKey === key);

export type MissingRequirement =
  | { code: "requiredDocMissing"; values: { label: string } }
  | { code: "creditReportOlderThanDays"; values: { days: number } }
  | { code: "creditReportDateMissing"; values: Record<string, never> };

export type CreditReportDateStatus = "ok" | "missing" | "stale";

export function getCreditReportDateStatus(
  creditReportDate: any,
  today: moment.Moment = moment(),
  maxAgeDays: number = DEFAULT_CREDIT_REPORT_MAX_AGE_DAYS,
): CreditReportDateStatus {
  if (!creditReportDate) return "missing";
  const reportDate = moment(creditReportDate);
  if (!reportDate.isValid()) return "missing";
  return today.clone().startOf("day").diff(reportDate.startOf("day"), "days") >
    maxAgeDays
    ? "stale"
    : "ok";
}

export interface ChecklistItem extends RequiredDocument {
  uploaded: boolean;
}

/**
 * The loan-level checklist: a snapshot of what the config requires for this
 * loan right now, with each item marked uploaded once a file is attached.
 */
export function buildChecklist(
  config: RequiredDocumentsConfig | undefined,
  loanInformation: any,
  documents: any[] | undefined,
): ChecklistItem[] {
  return getRequiredDocuments(config, loanInformation).map(
    ({ key, label }) => ({
      key,
      label,
      uploaded: Boolean(getUploadedRequiredDocument(documents, key)),
    }),
  );
}

/** { documentChecklist } to send with a documents PATCH, or {} when the feature is off. */
export const buildChecklistPayload = (
  config: RequiredDocumentsConfig | undefined,
  loanInformation: any,
  documents: any[],
) =>
  config && loanInformation
    ? { documentChecklist: buildChecklist(config, loanInformation, documents) }
    : {};

/** Files attached to a checklist item that is no longer in the checklist. */
export const getOrphanDocuments = (
  documents: any[] | undefined,
  checklist: RequiredDocument[],
) =>
  (documents ?? []).filter(
    (d) =>
      !d?.isDeleted &&
      d?.requiredDocumentKey &&
      !checklist.some((c) => c.key === d.requiredDocumentKey),
  );

/**
 * Returns one entry per unmet checklist item (a translation key plus its
 * values, rendered by the caller with t()); empty when the loan can be
 * submitted.
 */
export function getMissingRequirements(
  config: RequiredDocumentsConfig | undefined,
  checklist: RequiredDocument[] | undefined,
  documents: any[] | undefined,
  borrowerInformation: any,
  today: moment.Moment = moment(),
): MissingRequirement[] {
  const messages: MissingRequirement[] = [];
  const creditKey = config?.creditReportKey ?? DEFAULT_CREDIT_REPORT_KEY;
  const maxAge =
    config?.creditReportMaxAgeDays ?? DEFAULT_CREDIT_REPORT_MAX_AGE_DAYS;
  (checklist ?? []).forEach(({ key, label }) => {
    if (!getUploadedRequiredDocument(documents, key)) {
      messages.push({ code: "requiredDocMissing", values: { label } });
      return;
    }
    if (key === creditKey) {
      const status = getCreditReportDateStatus(
        borrowerInformation?.creditReportDate,
        today,
        maxAge,
      );
      if (status === "stale") {
        messages.push({
          code: "creditReportOlderThanDays",
          values: { days: maxAge },
        });
      } else if (status === "missing") {
        messages.push({ code: "creditReportDateMissing", values: {} });
      }
    }
  });
  return messages;
}

/** The rebuilt checklist when it differs from the stored one, else null. */
export function getChecklistChange(
  config: RequiredDocumentsConfig | undefined,
  loanInformation: any,
  documents: any[] | undefined,
  storedChecklist: ChecklistItem[] | undefined,
): ChecklistItem[] | null {
  if (!config || !loanInformation) return null;
  const desired = buildChecklist(config, loanInformation, documents);
  return JSON.stringify(desired) === JSON.stringify(storedChecklist)
    ? null
    : desired;
}

interface SyncChecklistArgs {
  config: RequiredDocumentsConfig | undefined;
  loanInformation: any;
  documents: any[];
  storedChecklist: ChecklistItem[] | undefined;
  deleteUrl?: string;
  deleteFromBucket: (url: string, body: { path: string }) => Promise<unknown>;
  deletedBy: string;
}

/**
 * Save-time cleanup: files attached to checklist items that are no longer in
 * the checklist are removed from the bucket and marked deleted. Returns the
 * documents + checklist to save, or null when nothing needs to change.
 */
export async function syncChecklistForSave({
  config,
  loanInformation,
  documents,
  storedChecklist,
  deleteUrl,
  deleteFromBucket,
  deletedBy,
}: SyncChecklistArgs): Promise<{
  documents: any[];
  documentChecklist: ChecklistItem[];
} | null> {
  if (!config || !loanInformation?.loanPurpose) return null;
  const desired = buildChecklist(config, loanInformation, documents);
  const orphans = getOrphanDocuments(documents, desired);
  if (
    !orphans.length &&
    JSON.stringify(desired) === JSON.stringify(storedChecklist)
  ) {
    return null;
  }
  const removed = new Set<string>();
  for (const doc of orphans) {
    try {
      if (deleteUrl) {
        await deleteFromBucket(deleteUrl, { path: doc.documentPath });
      } else {
        console.warn("fileDeleteUrl not configured; bucket file kept");
      }
      removed.add(doc.id);
    } catch (err) {
      // keep the record so the next Save retries the delete
      console.error("Error deleting document from bucket: ", err);
    }
  }
  const updatedDocuments = documents.map((d) =>
    removed.has(d.id)
      ? { ...d, isDeleted: true, deletedOn: new Date(), deletedBy }
      : d,
  );
  return {
    documents: updatedDocuments,
    documentChecklist: buildChecklist(config, loanInformation, updatedDocuments),
  };
}
